import { Box, Flex, Text } from '@chakra-ui/react'

import { LiveArtwork } from '../../../../services/Event/types'
import { formatBRL } from './format'
import { GavelIcon } from './GavelIcon'

interface ISoldOverlayProps {
  artwork: LiveArtwork
  /** `thumb` para o carrossel, `featured` para o quadro grande. */
  variant?: 'thumb' | 'featured'
}

const SIZES = {
  thumb: { gavel: '26px', label: '12px', value: '16px', buyer: '11px', gap: 1 },
  featured: {
    gavel: 'clamp(30px, 3.4vw, 72px)',
    label: 'clamp(14px, 1.5vw, 32px)',
    value: 'clamp(22px, 2.8vw, 58px)',
    buyer: 'clamp(12px, 1.15vw, 26px)',
    gap: 2
  }
}

/**
 * Selo das obras arrematadas, por cima da própria obra — no carrossel e no quadro em
 * destaque.
 *
 * A regra é uma só: **obra com `sale_status = 'sold'` sempre leva o selo**, onde quer que
 * ela apareça. Não depende de o leilão estar encerrado — quem grava esse status é o worker
 * `auctions:close-due` ao fechar o leilão, ou o admin numa venda pós-leilão.
 *
 * Duas consequências disso que não são óbvias:
 *
 * - **Reabrir o leilão não tira o selo.** O `toggleAuction` em modo reabertura só limpa o
 *   `auction_ends_at`; o `sale_status` das obras continua `sold`. Num leilão reaberto as
 *   já arrematadas voltam para o carrossel seladas, ao lado das que estão em disputa.
 * - **Venda pós-leilão não tem lance** — o comprador vem do `buyer_name`, ver a linha do "Comprador:".
 */
export function SoldOverlay({ artwork, variant = 'thumb' }: ISoldOverlayProps) {
  const size = SIZES[variant]

  // Mesmo critério do card do maior lance: o nome e, na falta dele, a comanda.
  const buyer = artwork.current_bid_participant_name || artwork.current_bid_tab_code

  return (
    <Flex
      position="absolute"
      inset={0}
      direction="column"
      align="center"
      justify="center"
      bg="blackAlpha.700"
      color="white"
      px={3}
      textAlign="center"
    >
      <Box mb={size.gap}>
        <GavelIcon size={size.gavel} />
      </Box>
      <Text fontSize={size.label} fontWeight="600" letterSpacing="0.06em">
        VENDIDA POR
      </Text>
      <Text fontSize={size.value} fontWeight="800" color="magenta.500" lineHeight="1.2">
        {formatBRL(artwork.current_bid)}
      </Text>
      {/*
        Buyer = top bid's author. A post-auction sale has no bids, so the API fills it with
        `buyer_name` shortened to first + last name (no other checkout data reaches the screen).
      */}
      {buyer && (
        <Text fontSize={size.buyer} color="whiteAlpha.900" noOfLines={1} maxW="100%">
          Comprador: {buyer}
        </Text>
      )}
    </Flex>
  )
}
